import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0107 from "../lesson-07/lesson";
import lesson0109 from "../lesson-09/lesson";
import lesson0117 from "../lesson-17/lesson";
import lesson0120 from "../lesson-20/lesson";

const linkLesson0107 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0107.id);
const linkLesson0109 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0109.id);
const linkLesson0117 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0117.id);
const linkLesson0120 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0120.id);

const theory = ` <h3 class=sectionHead>Eigenmodes vs resonances</h3>
<p>
   The problem of the resonance in a one dimensional cavity, i.e a closed tube under cutoff frequency, has been discussed in <a href="${linkLesson0107}" target="_blank">Lesson 1.7</a>. Indeed, the concept of eigenmode and that of resonance are two concepts often associated which often leads to confusion between them. The concept of proper mode, as the name suggests, is that of a passive system without source. Proper modes are possible ways for the system to vibrate given that any linear combination of different modes is also a possible solution. They are stationary solutions of a lossless problem. Nevertheless, losses can be included in a complex wave number as explained in <a href="${linkLesson0109}" target="_blank">Lesson 1.9</a> for plane waves in a tube. Solutions are then free damped vibration starting from an initial condition at time
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>.
<p>On the other hand, resonance exists only in the presence of a source: there is a resonance when the amplitude of a physical quantity such as the acoustic pressure or particle velocity reach a maximum somewhere. At resonance the injected power of the source is totally compensated by losses. It is then clear, as discussed in <a href="${linkLesson0107}" target="_blank">Lesson 1.7</a>, that the calculation of the response of a resonator implies the presence of losses without which the energy accumulate and vibration amplitude tends to infinite. In practice an insufficiently damped system may lead to destruction when excited at resonance frequency. When a source is coupled to a passive system, resonance frequencies can be quite close to the natural frequency of the passive system, in particular when the source is weakly coupled to the passive system (that is to say, the source is little affected by the behavior of the passive system). This is why the two concepts are often confused.
<h3 class=sectionHead>Field in a cavity</h3>
<p>
   As discussed in Lessons <a href="${linkLesson0117}" target="_blank">1.17</a> and <a href="${linkLesson0120}" target="_blank">1.20</a>, the solution of any acoustical problem in a cavity can be written a a linear combination of all modes
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mnq</mi>
         </mrow>
      </msub>
   </math>, that is
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=bold>r</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close stretchy=false>)</mo>
         <mo class=MathClass-rel>=</mo>
         <munder class=msub>
            <mrow>
               <mo>∑</mo>
            </mrow>
            <mrow>
               <mi>m</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>n</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>q</mi>
            </mrow>
         </munder>
         <msub>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>mnq</mi>
            </mrow>
         </msub>
         <msub>
            <mrow>
               <mi>Ψ</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>mnq</mi>
            </mrow>
         </msub>
         </math>
      <td class=eq-no>(1)
</table>
<p>In a system with a source, modes should include damping so that their amplitude remain always finite. A consequence is that it is also never completely null. So, when source frequency coincide with a proper frequency, amplitude might be high and when it do not coincide with any proper frequency it might be small. In any case the field is described at any frequency by the superposition of all modes. Obviously at resonance one mode is highly predominant so that the field is reasonably well described considering a single mode or a group of modes if degenerated. On the contrary far from a resonance frequency a large number of modes are needed do describe the field.
<p>
   The spatial distribution of the sources is also important because this determines the amplitudes of the modes
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mnq</mi>
         </mrow>
      </msub>
   </math>
   . Consider for example a source placed on a given surface
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   inside the cavity which moves with a normal velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold>r’</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=bold>r’</mi>
   <mo class=MathClass-rel>∈</mo>
   <mi>S</mi>
   </math>. The boundary condition writes on
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>∂p</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=bold>r’</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
      <mi mathvariant=italic>∂n</mi>
   </mrow>
      </mfrac>
   <msub>
      <mrow>
         <mo class=MathClass-close>)</mo>
      </mrow>
      <mrow>
         <mi>n</mi>
      </mrow>
   </msub>
   </math>
   <mo class=MathClass-rel>=</mo>
   <mi mathvariant=italic>jkρc</mi>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>n</mi>
      </mrow>
   </msub>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=bold>r’</mi>
   <mo class=MathClass-close>)</mo></math>. Pressure can be developped on the modes and using the orthogonality on the modes the amplitude of the modes can be deduced:
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mnq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jkρc</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mo>∫</mo>
                     </mrow>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Ψ</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>mnq</mi>
                     </mrow>
                  </msub>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>mnq</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mnq</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msub>
                     <mrow>
                        <mi>Ψ</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>mnq</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂n</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mnq</mi>
         </mrow>
      </msub>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>S</mi>
   </math>
   . In some situations, it appear that for some modes
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mnq</mi>
         </mrow>
      </msub>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>S</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   which means that the mode is not coupled with the source. This is the case, for example, if a point source is placed on a velocity node of a given mode in the cavity. Also, if the problem has some symmetries, antisymmetrical modes will not be excited. As well if the problem has antisymmetries, symmetrical modes will not be excited.
   <h3 class=sectionHead>References</h3>
   <dl class=thebibliography>
     <dt>[1]
     <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
     <dt>[2]
     <dd>M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
     <dt>[3]
     <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
     <dt>[4]
     <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
     <dt>[5]
     <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
     <dt>[6]
     <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
   </dl>`;

export default theory;
